import React, { useState } from 'react';
import type { IconType } from 'react-icons';
import {
  SiInstagram,
  SiTiktok,
  SiYoutube,
  SiX,
  SiFacebook,
} from 'react-icons/si';
import { FaGlobe, FaLinkedinIn } from 'react-icons/fa6';
import { HiSparkles } from 'react-icons/hi2';

import {
  STAR_MAP,
  formatCompactNumber,
  getInitials,
  timeAgo,
} from 'utils/formatters';
import { computeProfileBadges } from 'utils/profileBadges';
import { PresenceDot } from 'components/PresenceDot';
import { TrustBadge } from 'components/TrustBadge';
import * as trustBadgeStyles from 'components/TrustBadge/styles';
import { usePublicPresence } from 'hooks/usePublicPresence';
import { BrandColors } from 'constants/brand-colors';
import { Colors } from 'constants/ui';

import * as styled from '../styles/header';

type Props = {
  provider: Kudable.Provider;
  profileToken?: string;
  reputation: Kudable.Reputation;
  responseTimeHours: number | null;
  intents: Profile.IntentKey[];
  subscriptionActive: boolean;
  onPickIntent: (key: Profile.IntentKey) => void;
  onOpenChooser: () => void;
};

const TIKTOK_GLITCH_OFFSET_PX = 1.5;
const SOCIAL_ICON_SIZE_PX = 16;

const SOCIAL_ICON_SOURCES: Record<
  string,
  { label: string; Icon: IconType; color: string }
> = {
  website: { label: 'Website', Icon: FaGlobe, color: Colors.darkSlate },
  instagram: {
    label: 'Instagram',
    Icon: SiInstagram,
    color: BrandColors.instagram,
  },
  tiktok: { label: 'TikTok', Icon: SiTiktok, color: BrandColors.tiktok },
  youtube: { label: 'YouTube', Icon: SiYoutube, color: BrandColors.youtube },
  twitter: { label: 'X', Icon: SiX, color: BrandColors.x },
  linkedin: {
    label: 'LinkedIn',
    Icon: FaLinkedinIn,
    color: BrandColors.linkedin,
  },
  facebook: {
    label: 'Facebook',
    Icon: SiFacebook,
    color: BrandColors.facebook,
  },
};

const tileBackgroundFor = (key: string, fallback: string): string => {
  if (key === 'instagram') return BrandColors.instagramGradient;
  return fallback;
};

const PROMOTES_LABELS: Record<string, string> = {
  beauty: 'Beauty & skincare',
  fashion: 'Fashion & apparel',
  wellness: 'Health & wellness',
  fitness: 'Fitness & sports',
  food: 'Food & beverage',
  tech: 'Tech & gadgets',
  tools: 'Tools & software',
  apps: 'Apps & SaaS',
  courses: 'Courses & education',
  books: 'Books & media',
  travel: 'Travel & hospitality',
  home: 'Home & lifestyle',
  finance: 'Finance & investing',
  gaming: 'Gaming',
  products: 'Physical products',
  services: 'Services',
};

const DIRECT_LABELS: Record<
  Exclude<Profile.IntentKey, 'message'>,
  (name: string) => string
> = {
  book: (name) => `Book ${name}`,
  project: (name) => `Hire ${name}`,
  collab: (name) => `Collaborate with ${name}`,
};

const ActionRow = ({
  accentColor,
  firstName,
  intents,
  onPickIntent,
  onOpenChooser,
  responseTimeHours,
}: {
  accentColor: string;
  firstName: string;
  intents: Profile.IntentKey[];
  onPickIntent: (key: Profile.IntentKey) => void;
  onOpenChooser: () => void;
  responseTimeHours: number | null;
}) => {
  if (intents.length === 0) return null;

  const primaryIntents = intents.filter(
    (intentKey): intentKey is Exclude<Profile.IntentKey, 'message'> =>
      intentKey !== 'message',
  );

  let label: string;
  let onClick: () => void;

  if (primaryIntents.length === 0) {
    label = `Send ${firstName} a message`;
    onClick = () => onPickIntent('message');
  } else if (primaryIntents.length === 1) {
    const onlyIntent = primaryIntents[0];
    label = DIRECT_LABELS[onlyIntent](firstName);
    onClick = () => onPickIntent(onlyIntent);
  } else {
    label = `Work with ${firstName}`;
    onClick = onOpenChooser;
  }

  return (
    <styled.ActionRowWrap>
      <styled.ActionRowButtons>
        <styled.PrimaryAction $accent={accentColor} onClick={onClick}>
          {label}
        </styled.PrimaryAction>
      </styled.ActionRowButtons>
      {responseTimeHours != null && responseTimeHours > 0 ? (
        <styled.ResponseTimeText>
          Usually responds within {Math.round(responseTimeHours)}h
        </styled.ResponseTimeText>
      ) : null}
    </styled.ActionRowWrap>
  );
};

const PromotesDisclosure = ({
  promotes,
  promotesNiche,
  accentColor,
}: {
  promotes: string[];
  promotesNiche: string | null | undefined;
  accentColor: string;
}) => {
  return (
    <styled.PromotesCard>
      <styled.PromotesHeader>
        <styled.PromotesLabel>Promotes</styled.PromotesLabel>
        {promotesNiche ? (
          <styled.PromotesNiche $accent={accentColor}>
            #{promotesNiche}
          </styled.PromotesNiche>
        ) : null}
      </styled.PromotesHeader>

      {promotes.length > 0 ? (
        <styled.PromotesChips>
          {promotes.map((category) => (
            <styled.PromotesChip key={category}>
              {PROMOTES_LABELS[category] ?? category}
            </styled.PromotesChip>
          ))}
        </styled.PromotesChips>
      ) : null}
    </styled.PromotesCard>
  );
};

const SocialIconContent = ({
  Icon,
  platform,
}: {
  Icon: IconType;
  platform: string;
}) => {
  if (platform === 'tiktok') {
    return (
      <styled.TiktokWrap>
        <styled.TiktokLayer $offset={-TIKTOK_GLITCH_OFFSET_PX}>
          <Icon size={SOCIAL_ICON_SIZE_PX} color={BrandColors.tiktokCyan} />
        </styled.TiktokLayer>
        <styled.TiktokLayer $offset={TIKTOK_GLITCH_OFFSET_PX}>
          <Icon size={SOCIAL_ICON_SIZE_PX} color={BrandColors.tiktokMagenta} />
        </styled.TiktokLayer>
        <styled.TiktokLayer $offset={0}>
          <Icon size={SOCIAL_ICON_SIZE_PX} color={Colors.white} />
        </styled.TiktokLayer>
      </styled.TiktokWrap>
    );
  }
  return <Icon size={SOCIAL_ICON_SIZE_PX} color={Colors.white} />;
};

export const ProfileHeader = ({
  provider,
  profileToken,
  reputation,
  responseTimeHours,
  intents,
  subscriptionActive,
  onPickIntent,
  onOpenChooser,
}: Props) => {
  const livePresence = usePublicPresence(profileToken);
  const online = livePresence?.online ?? provider.online;
  const lastSeenAt = livePresence?.last_seen_at ?? provider.last_seen_at;
  const [avatarFailed, setAvatarFailed] = useState(false);
  const initials = getInitials(provider.first_name, provider.last_name);
  const isBusiness =
    provider.theme_preset === 'business' || provider.company_mode;
  const useLogoAsAvatar = isBusiness && !!provider.logo_url;
  const mainImageUrl = useLogoAsAvatar
    ? provider.logo_url
    : provider.avatar_url;
  const showImage = mainImageUrl && !avatarFailed;

  const accentColor = provider.accent_color ?? Colors.accent;
  const customBorderColor = provider.avatar_border_color || null;
  const isBusinessTheme = provider.theme_preset === 'business';
  const socialLinks = provider.social_links ?? {};
  const socialKeys = Object.keys(socialLinks).filter(
    (socialKey) => SOCIAL_ICON_SOURCES[socialKey] && socialLinks[socialKey],
  );
  const isCreatorMode = provider.provider_mode === 'creator';
  const promotes = isCreatorMode ? (provider.promotes_categories ?? []) : [];
  const promotesNiche = isCreatorMode ? provider.promotes_niche : null;
  const openToCollabs = isCreatorMode && !!provider.open_to_collabs;
  const featuredTeam = provider.featured_team_members ?? [];
  const firstName = provider.first_name || provider.business_name || 'them';

  const badges = computeProfileBadges({
    score: reputation.score,
    reviewCount: reputation.review_count,
    insertedAt: provider.inserted_at,
    acceptsBookings: provider.accepts_bookings,
    subscriptionActive,
  });

  const hasBanner = !!provider.banner_url;

  const resolvedBorderColor =
    customBorderColor ??
    (hasBanner
      ? Colors.white
      : isBusinessTheme
        ? Colors.border
        : 'transparent');
  const borderWidthPx = customBorderColor ? 5 : hasBanner ? 5 : 2;
  const avatarRounded = !isBusinessTheme;

  return (
    <div>
      <styled.Banner $url={provider.banner_url ?? undefined} />
      <styled.Identity $hasBanner={hasBanner}>
        <styled.Avatar
          $accent={accentColor}
          $rounded={avatarRounded}
          $borderColor={resolvedBorderColor}
          $borderWidth={borderWidthPx}
        >
          {showImage ? (
            <styled.AvatarImage
              src={mainImageUrl!}
              alt=""
              onError={() => setAvatarFailed(true)}
              $rounded={avatarRounded}
              $logo={useLogoAsAvatar}
            />
          ) : (
            initials
          )}
        </styled.Avatar>

        <styled.NameRow>
          <styled.Name>
            {provider.company_mode && provider.business_name
              ? provider.business_name
              : `${provider.first_name} ${provider.last_name}`}
          </styled.Name>
          {provider.logo_url && !useLogoAsAvatar ? (
            <styled.SecondaryLogo src={provider.logo_url} alt="" />
          ) : null}
        </styled.NameRow>

        <PresenceDot online={online} lastSeenAt={lastSeenAt} />

        {provider.company_mode
          ? provider.workplace && (
              <styled.Title>{provider.workplace}</styled.Title>
            )
          : provider.title && (
              <styled.Title>
                {provider.title}
                {provider.workplace ? ` · ${provider.workplace}` : ''}
              </styled.Title>
            )}

        {provider.tagline ? (
          <styled.Tagline>{provider.tagline}</styled.Tagline>
        ) : null}

        {badges.length > 0 ? (
          <trustBadgeStyles.Row>
            {badges.map((badge) => (
              <TrustBadge key={badge.key} badge={badge} />
            ))}
          </trustBadgeStyles.Row>
        ) : null}

        <styled.MetaRow>
          <styled.JoinedText>
            Joined {timeAgo(provider.inserted_at)}
          </styled.JoinedText>
          {openToCollabs ? (
            <>
              <styled.MetaSeparator>·</styled.MetaSeparator>
              <styled.CollabBadge $accent={accentColor}>
                <HiSparkles size={13} color={accentColor} />
                Open to collabs
              </styled.CollabBadge>
            </>
          ) : null}
        </styled.MetaRow>

        {socialKeys.length > 0 ? (
          <styled.SocialIconsRow>
            {socialKeys.map((key) => {
              const iconSource = SOCIAL_ICON_SOURCES[key];
              return (
                <styled.SocialTile
                  key={key}
                  href={socialLinks[key]}
                  target="_blank"
                  rel="noreferrer noopener"
                  $background={tileBackgroundFor(key, iconSource.color)}
                  aria-label={iconSource.label}
                >
                  <SocialIconContent Icon={iconSource.Icon} platform={key} />
                </styled.SocialTile>
              );
            })}
          </styled.SocialIconsRow>
        ) : null}
      </styled.Identity>

      <ActionRow
        accentColor={accentColor}
        firstName={firstName}
        intents={intents}
        onPickIntent={onPickIntent}
        onOpenChooser={onOpenChooser}
        responseTimeHours={responseTimeHours}
      />

      <styled.ScoreCard $accent={accentColor}>
        {(reputation.jobs_completed ?? 0) > 0 ? (
          <styled.JobsCompleted $accent={accentColor}>
            <styled.JobsCompletedValue $accent={accentColor}>
              {formatCompactNumber(reputation.jobs_completed ?? 0)}
            </styled.JobsCompletedValue>
            <styled.JobsCompletedLabel $accent={accentColor}>
              Jobs completed
            </styled.JobsCompletedLabel>
          </styled.JobsCompleted>
        ) : null}
        <styled.ScoreLabel $accent={accentColor}>
          Reputation Score
        </styled.ScoreLabel>
        <styled.ScoreValue $accent={accentColor}>
          {reputation.score ? reputation.score.toFixed(1) : '—'}
        </styled.ScoreValue>
        <styled.ScoreStars>
          {reputation.score
            ? (STAR_MAP[Math.round(reputation.score)] ?? '★★★★★')
            : 'No reviews yet'}
        </styled.ScoreStars>
        <styled.ScoreBased $accent={accentColor}>
          {reputation.review_count > 0
            ? `Based on ${formatCompactNumber(reputation.review_count)} reviews`
            : 'Be the first to get reviewed'}
        </styled.ScoreBased>
      </styled.ScoreCard>

      {(promotes.length > 0 || promotesNiche) && (
        <PromotesDisclosure
          promotes={promotes}
          promotesNiche={promotesNiche}
          accentColor={accentColor}
        />
      )}

      {featuredTeam.length > 0 ? (
        <styled.FeaturedTeamWrap>
          <styled.FeaturedTeamLabel>Featured team</styled.FeaturedTeamLabel>
          <styled.FeaturedTeamRow>
            {featuredTeam.map((member) => (
              <styled.FeaturedTeamMember key={member.id}>
                <styled.FeaturedAvatar
                  $accent={accentColor}
                  $url={member.avatar_url ?? undefined}
                >
                  {!member.avatar_url
                    ? getInitials(
                        member.first_name ?? '',
                        member.last_name ?? '',
                      )
                    : null}
                </styled.FeaturedAvatar>
                <styled.FeaturedName>
                  {member.first_name ?? ''} {member.last_name ?? ''}
                </styled.FeaturedName>
                {member.role ? (
                  <styled.FeaturedRole>{member.role}</styled.FeaturedRole>
                ) : null}
              </styled.FeaturedTeamMember>
            ))}
          </styled.FeaturedTeamRow>
        </styled.FeaturedTeamWrap>
      ) : null}
    </div>
  );
};
